/**
 * File responsible for mapping the redux state.
 */

import { combineReducers, Dispatch, Action, AnyAction } from 'redux';
import { all, fork } from 'redux-saga/effects';
import { connectRouter, RouterState } from 'connected-react-router';
// import { History } from 'history';

// Types
import { AuthState } from './auth/types';
import { StoredRouteState } from './storedRoute/types';
import { RolesState } from './roles/types';
import { AppCommonState } from './common/types';
import { SupportState } from './support/types';
import { AnnouncementsState } from './announcements/types';

// Sagas
import authSaga from './auth/sagas';
import rolesSaga from './roles/sagas';
import commonSaga from './common/sagas';
import supportSaga from './support/sagas';
import announcementsSaga from './announcements/sagas';

// Reducers
import { authReducer } from './auth/reducer';
import { storedRouteReducer } from './storedRoute/reducer';
import { rolesReducer } from './roles/reducer';
import { commonReducer } from './common/reducer';
import { supportReducer } from './support/reducer';
import { announcementsReducer } from './announcements/reducer';

// The top-level state object
export interface ApplicationState {
    app: AppCommonState;
    auth: AuthState;
    storedRoute: StoredRouteState;
    roles: RolesState;
    support: SupportState;
    announcements: AnnouncementsState;

    router: RouterState;
}

// Additional props for connected React components. This prop is passed by default with `connect()`
export interface ConnectedReduxProps<A extends Action = AnyAction> {
    dispatch: Dispatch<A>;
}

// Whenever an action is dispatched, Redux will update each top-level application state property
// using the reducer with the matching name. It's important that the names match exactly, and that
// the reducer acts on the corresponding ApplicationState property type.
export const createRootReducer = (history: History) =>
    combineReducers({
        app: commonReducer,
        auth: authReducer,
        storedRoute: storedRouteReducer,
        roles: rolesReducer,
        support: supportReducer,
        announcements: announcementsReducer,

        router: connectRouter(history),
    });

// Here we use `redux-saga` to trigger actions asynchronously. `redux-saga` uses something called a
// "generator function", which you can read about here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function*
export function* rootSaga() {
    // yield all([fork(heroesSaga), fork(teamsSaga)]);
    yield all([
        fork(authSaga),
        fork(rolesSaga),
        fork(commonSaga),
        fork(supportSaga),
        fork(announcementsSaga),
    ]);
}
